import React from 'react';
import { Card, Link } from '../ui';
import useTheme from '@material-ui/core/styles/useTheme';
import styled, { ThemeProvider } from 'styled-components';
import { useTranslation } from 'react-i18next';
import ViewNewIcon from '../../icons/ViewNew';
import ThumbsUpIcon from '../../icons/ThumbsUp';
import { getFullName } from '../../helpers/users';
import { Submission } from '../../store/submission/types';
import Alert from '../../icons/Alert';

interface ReviewerSubmission extends Submission {
  conflict: boolean;
  keywords: string;
  main_area: string;
  bid_option_id: number;
  authors_str: string;
  affinity: number;
}

interface Props {
  submission: ReviewerSubmission;
  canViewAuthors: boolean;
  keywordList: string;
  selectedBid: string;
  hasKeywords: boolean;
  onViewSubmissionBidDetail: () => void;
  onEditBid: () => void;
}

const SubmissionBiddingCard: React.FC<Props> = ({
  submission,
  canViewAuthors,
  keywordList,
  selectedBid,
  hasKeywords,
  onViewSubmissionBidDetail,
  onEditBid,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <ThemeProvider theme={theme}>
      <StyledCard>
        <p className="text-sm font-bold mb-4 line-clamp">{submission.title}</p>
        {hasKeywords && (
          <p className="text-sm mb-4">
            <span className="font-bold">{t('Affinity') + ': '}</span> {submission.affinity}
          </p>
        )}
        {submission.authors.length > 0 && canViewAuthors && (
          <p className="text-sm line-clamp">
            <span className="font-bold">{t('Authors') + ': '}</span>
            {submission.authors.map((author) => `${getFullName(author)}`).join(', ')}
          </p>
        )}
        {hasKeywords && (
          <p className="text-sm mb-4 line-clamp">
            <span className="font-bold">{t('Keywords') + ': '}</span>
            {keywordList != '' ? keywordList : t('No keywords')}
          </p>
        )}

        <div className="text-sm absolute bottom-4 flex justify-between w-full left-0 px-5 flex-wrap actions-wrapper items-center">
          <div className="mt-1 simple-action-wrapper">
            <Link
              className="cursor-pointer text-sm flex items-center"
              onClick={() => {
                onViewSubmissionBidDetail();
              }}
            >
              <ViewNewIcon />
              <span className="ml-1">{t('View submission')}</span>
            </Link>
          </div>

          <div className="mt-1 simple-action-wrapper">
            <Link
              className="cursor-pointer text-sm flex items-center mobileCtrlBidSubmissionThumbsUp"
              onClick={onEditBid}
            >
              {submission.conflict ? (
                <>
                  <Alert color={theme.palette.primary.main} />
                  <span className="ml-1">{t('Conflict declared')}</span>
                </>
              ) : submission.bid_option_id !== null ? (
                <span className="mobileCtrlBidOptionName">{selectedBid}</span>
              ) : (
                <>
                  <ThumbsUpIcon />
                  <span className="ml-1">{t('Bid submission')}</span>
                </>
              )}
            </Link>
          </div>
        </div>
      </StyledCard>
    </ThemeProvider>
  );
};

export default SubmissionBiddingCard;

const StyledCard = styled(Card)`
  min-height: 16.6rem;
  border-radius: 1rem;
  margin-bottom: 1.5rem;

  .line-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
